



































































































































import { defineComponent, PropType, ref, computed, useContext } from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import { uniq, sortBy } from 'lodash-es';
import ProductDetailInfoCell from '~/components/pages/Product/Detail/ProductDetailInfoCell.vue';
import ProductDetailSimilarProductsModalBody from '~/components/pages/Product/Detail/ProductDetailSimilarProductsModalBody.vue';
import ProductDetailKeywords from '~/components/pages/Product/ProductDetailKeywords.vue';
import AppLink from '~/components/partials/App/AppLink.vue';
import AppModal from '~/components/partials/App/AppModal.vue';
import ProducingAreaFlag from '~/components/partials/ProducingArea/ProducingAreaFlag.vue';
import { useMatchMedia } from '~/composables/useMatchMedia';
import { useWOVNio } from '~/composables/wovn/useWOVNio';
import { PRODUCT_COLOR_ID, PRODUCT_CELLAR_INFO, PRODUCT_PARAM_QUERIES, PAYMENT_METHOD_ID } from '~/constants';
import { url } from '~/utils';
import { formatKeyValueArray, getProductVariant, formatProducingArea } from '~/utils/modules/products';
import { ProductShowECResponseSchema } from '~/types/modules/product';
import { ProductCategoryId, TailleventType, DisplayLang, BooleanLike } from '~/enums';

export default defineComponent({
  components: {
    ProductDetailInfoCell,
    ProductDetailSimilarProductsModalBody,
    ProducingAreaFlag,
    AppLink,
    AppModal,
    ProductDetailKeywords,
  },
  props: {
    ecResponse: {
      type: Object as PropType<ProductShowECResponseSchema>,
      required: true,
    },
  },
  setup(props) {
    const isOpenSimilarProductsModal = ref(false);
    const { i18n } = useContext();
    const { currentLang } = useWOVNio();
    const { isMobile } = useMatchMedia();
    const productCategoryId = computed(() => props.ecResponse.categories[0]?.id || ProductCategoryId.WINE);
    const isBottle = computed(() =>
      [
        ProductCategoryId.WINE,
        ProductCategoryId.WINE_SET,
        ProductCategoryId.GIFT_SET,
        ProductCategoryId.OTHER_ALCOHOL,
        ProductCategoryId.NON_ALCOHOL,
      ].includes(productCategoryId.value)
    );
    const SPARKLING_ID_LIST = [
      Number(PRODUCT_COLOR_ID.SPARKLING_WHITE),
      Number(PRODUCT_COLOR_ID.SPARKLING_ROSE),
      Number(PRODUCT_COLOR_ID.SPARKLING_RED),
    ];
    const data = computed(() => {
      const {
        attributes,
        best_priced_variant_id,
        product_variants,
        master_settings,
        categories,
        is_taillevant,
        shipping_method_settings,
        not_allowed_payment_methods,
        size_unit,
        size_amount,
        is_cellar_in,
      } = props.ecResponse;

      const cellarInFeatures = uniq(product_variants.flatMap((variant) => variant.cellar_in_info.cellar_in_feature));
      const cellarInDetails = uniq(product_variants.flatMap((variant) => variant.cellar_in_info.feature_detail)).filter(
        (item) => item
      );

      const formattedAttributes = formatKeyValueArray(attributes);
      const formattedVariantAttributes = formatKeyValueArray(product_variants[0].attributes);
      const bestPricedVariant = getProductVariant(Number(best_priced_variant_id), product_variants);
      const listPath = url('ITEM_LIST');
      // ワインカテゴリのクエリは不要
      const categoryIds = categories.map(({ id }) => id).filter((id) => id !== ProductCategoryId.WINE);
      // ワインカテゴリの時は全カラーのクエリをつける
      const allColorQuery = categories.some(({ id }) => id === ProductCategoryId.WINE)
        ? [
            PRODUCT_COLOR_ID.RED,
            PRODUCT_COLOR_ID.WHITE,
            PRODUCT_COLOR_ID.ROSE,
            PRODUCT_COLOR_ID.MIX,
            PRODUCT_COLOR_ID.ORANGE,
            PRODUCT_COLOR_ID.SPARKLING_WHITE,
            PRODUCT_COLOR_ID.SPARKLING_ROSE,
            PRODUCT_COLOR_ID.SPARKLING_RED,
          ]
        : undefined;
      const colorId = () => {
        if (master_settings?.color?.name === 'ミックス') {
          return '8';
        } else if (master_settings?.color?.id && SPARKLING_ID_LIST.includes(master_settings?.color?.id)) {
          return '99';
        }
        return master_settings?.color?.id?.toString();
      };
      const colorName = () => {
        if (master_settings?.color?.id && SPARKLING_ID_LIST.includes(master_settings?.color?.id)) {
          return 'スパークリング';
        }
        return master_settings?.color?.name;
      };

      return {
        category: categories.map((item, index) => {
          return {
            link: {
              path: listPath,
              query: { _ctg: categoryIds.slice(0, index + 1), _color: allColorQuery, _qty: BooleanLike.TRUE },
            },
            name: item.name,
          };
        }),
        type: master_settings.color && {
          link: {
            path: listPath,
            query: {
              _color: colorId(),
              _qty: BooleanLike.TRUE,
            },
          },
          name: colorName(),
        },
        producer: master_settings.producer && {
          // リンク先は生産者特集
          link: url('PRODUCER_DETAIL', { id: master_settings.producer.id }),
          nameEn: master_settings.producer.name,
          name: master_settings.producer.name_kana,
        },
        producingArea: formatProducingArea(master_settings.producing_areas),
        nativeArea: master_settings.native_area
          ? {
              name: master_settings.native_area?.name,
              link: {
                path: url('ITEM_LIST'),
                query: {
                  [PRODUCT_PARAM_QUERIES.AOC]: master_settings.native_area.id,
                  [PRODUCT_PARAM_QUERIES.QTY]: BooleanLike.TRUE,
                },
              },
            }
          : undefined,
        brand:
          product_variants.length === 1
            ? product_variants[0].brands.length > 0
              ? product_variants[0].brands.map((brand) => ({
                  link: {
                    path: listPath,
                    query: { [PRODUCT_PARAM_QUERIES.VARIETY]: brand.id?.toString(), _qty: BooleanLike.TRUE },
                  },
                  name: brand.name,
                  rate: brand.rate,
                }))
              : master_settings.brands.map((brand) => ({
                  link: {
                    path: listPath,
                    query: { [PRODUCT_PARAM_QUERIES.VARIETY]: brand.id?.toString(), _qty: BooleanLike.TRUE },
                  },
                  name: brand.name,
                  rate: brand.rate,
                }))
            : master_settings.brands.map((brand) => ({
                link: {
                  path: listPath,
                  query: { [PRODUCT_PARAM_QUERIES.VARIETY]: brand.id?.toString(), _qty: BooleanLike.TRUE },
                },
                name: brand.name,
                rate: brand.rate,
              })) ?? [],
        bio: (() => {
          const {
            natural_wine_biologic_flag,
            natural_wine_biodynamie_flag,
            natural_wine_natural_flag,
            biologic_certification_authority = '',
            biodynamie_certification_authority = '',
            natural_certification_authority = '',
          } = formatKeyValueArray(attributes);
          if (natural_wine_biologic_flag === 'true') {
            return { class: 'ビオロジック', authority: biologic_certification_authority };
          } else if (natural_wine_biodynamie_flag === 'true') {
            return { class: 'ビオディナミ', authority: biodynamie_certification_authority };
          } else if (natural_wine_natural_flag === 'true') {
            return { class: 'ナチュラル', authority: natural_certification_authority };
          }
        })(),
        class: formattedAttributes?.class,
        taillevent: is_taillevant,
        size: size_amount ? `${size_amount}${size_unit || 'ml'}` : undefined,
        expirationDate:
          bestPricedVariant?.expiration_date && dayjs(bestPricedVariant?.expiration_date).tz('Asia/Tokyo').format('ll'),
        recommendedDish: formattedAttributes?.recommended_dish,
        shipment: props.ecResponse.product_variants?.some((variant) => variant.price_settings?.is_free_shipping),
        onlyCoolShipment: shipping_method_settings?.cool_only_flag,
        onlyNormalShipment: shipping_method_settings?.normal_only_flag,
        paymentCaution:
          not_allowed_payment_methods.length > 0
            ? i18n
                .t('Item-03.productInfo.body.notAllowedPaymentMethods', {
                  methods: not_allowed_payment_methods
                    .map((method) => {
                      if (method.id === PAYMENT_METHOD_ID.ECON_PAY) {
                        return i18n.t('Item-03.productInfo.body.notAllowedEcon');
                      }
                      return method.name;
                    })
                    .join('、'),
                })
                .toString()
            : '',
        isSinglePiece: product_variants.some((variant) => variant.single_piece_flag),
        isAvailableRentalCellar: is_cellar_in,
        cellarInInfo: [
          ...sortBy(cellarInFeatures).map((id) => PRODUCT_CELLAR_INFO[id as keyof typeof PRODUCT_CELLAR_INFO]),
          ...cellarInDetails,
        ].join('、'),
        /** 子商品情報：単一ヴィンテージの場合のみ表示する */
        awardInformation: product_variants.length === 1 && product_variants[0].award_information,
        alcoholDegree: product_variants.length === 1 && product_variants[0].alcohol_degree,
        stopper: product_variants.length === 1 && formattedVariantAttributes?.stopper,
      };
    });

    const isShownProducerNameJa = computed(
      () => !data.value.producer?.nameEn || currentLang.value.code === DisplayLang.JA
    );

    return {
      props,
      isOpenSimilarProductsModal,
      isBottle,
      isMobile,
      data,
      TailleventType,
      isShownProducerNameJa,
    };
  },
});
